import { useState, useEffect } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';
import { 
  Search, 
  Building2, 
  Eye,
  CreditCard,
  ToggleLeft,
  ToggleRight,
  Key,
  Plus,
  RefreshCw,
  Loader2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminRestaurants() {
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [restaurantDetails, setRestaurantDetails] = useState(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newRestaurant, setNewRestaurant] = useState({
    name: '',
    owner_name: '',
    owner_email: '',
    timezone: 'America/Mexico_City',
    currency: 'MXN'
  });

  useEffect(() => {
    fetchRestaurants();
    
    // Check for Stripe return
    const urlParams = new URLSearchParams(window.location.search);
    const stripeReturn = urlParams.get('stripe_return');
    const restaurantId = urlParams.get('restaurant_id');
    
    if (stripeReturn && restaurantId) {
      // Clean URL
      window.history.replaceState({}, '', window.location.pathname);
      
      if (stripeReturn === 'success') {
        toast.success('Proceso de Stripe completado. Verificando estado...');
        // Check stripe status after a moment
        setTimeout(async () => {
          const token = localStorage.getItem('admin_token');
          const response = await fetch(`${API_URL}/api/admin/stripe-connect/status/${restaurantId}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (response.ok) {
            const data = await response.json();
            if (data.onboarding_complete) {
              toast.success('Cuenta Stripe configurada exitosamente');
            } else {
              toast.info('Onboarding de Stripe pendiente de completar');
            }
            fetchRestaurants();
          }
        }, 1000);
      } else if (stripeReturn === 'refresh') {
        toast.info('El enlace de Stripe expiró. Por favor intenta de nuevo.');
      }
    }
  }, []);

  const fetchRestaurants = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const params = searchTerm ? `?search=${encodeURIComponent(searchTerm)}` : '';
      const response = await fetch(`${API_URL}/api/admin/restaurants${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setRestaurants(data);
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const debounce = setTimeout(fetchRestaurants, 300);
    return () => clearTimeout(debounce);
  }, [searchTerm]);

  const viewDetails = async (restaurantId) => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_URL}/api/admin/restaurants/${restaurantId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setRestaurantDetails(data);
        setShowDetailsDialog(true);
      }
    } catch (error) {
      toast.error('Error al cargar detalles');
    }
  };

  const toggleActive = async (restaurantId, currentStatus) => {
    try {
      const token = localStorage.getItem('admin_token');
      await fetch(`${API_URL}/api/admin/restaurants/${restaurantId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ is_active: !currentStatus })
      });
      toast.success(currentStatus ? 'Restaurante desactivado' : 'Restaurante activado');
      fetchRestaurants();
    } catch (error) {
      toast.error('Error al actualizar');
    }
  };

  const connectStripe = async (restaurantId) => {
    try {
      const token = localStorage.getItem('admin_token');
      
      // First, create a Stripe Connect account
      const createResponse = await fetch(`${API_URL}/api/admin/stripe-connect/create-account?restaurant_id=${restaurantId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      
      const createData = await createResponse.json();
      
      if (!createResponse.ok) {
        toast.error(createData.detail || 'Error al crear cuenta Stripe');
        return;
      }
      
      // Now create onboarding link
      const currentUrl = window.location.origin;
      const onboardingResponse = await fetch(`${API_URL}/api/admin/stripe-connect/onboarding-link`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          restaurant_id: restaurantId,
          return_url: `${currentUrl}/admin/restaurants?stripe_return=success&restaurant_id=${restaurantId}`,
          refresh_url: `${currentUrl}/admin/restaurants?stripe_return=refresh&restaurant_id=${restaurantId}`
        })
      });
      
      const onboardingData = await onboardingResponse.json();
      
      if (onboardingResponse.ok && onboardingData.onboarding_url) {
        toast.success('Redirigiendo a Stripe para completar el registro...');
        window.open(onboardingData.onboarding_url, '_blank');
      } else {
        toast.error('Error al generar enlace de onboarding');
      }
      
      fetchRestaurants();
    } catch (error) {
      console.error('Stripe Connect error:', error);
      toast.error('Error al conectar Stripe');
    }
  };
  
  const checkStripeStatus = async (restaurantId) => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_URL}/api/admin/stripe-connect/status/${restaurantId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.onboarding_complete) {
          toast.success('Cuenta Stripe verificada y activa');
        } else if (data.has_account) {
          toast.info('Onboarding de Stripe pendiente');
        } else {
          toast.info('Sin cuenta Stripe');
        }
        fetchRestaurants();
      }
    } catch (error) {
      toast.error('Error al verificar estado');
    }
  };
  
  const openStripeDashboard = async (restaurantId) => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_URL}/api/admin/stripe-connect/dashboard-link/${restaurantId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.dashboard_url) {
          window.open(data.dashboard_url, '_blank');
        }
      } else {
        toast.error('Error al obtener enlace del dashboard');
      }
    } catch (error) {
      toast.error('Error al abrir dashboard de Stripe');
    }
  };

  const generateApiKey = async (restaurantId) => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_URL}/api/admin/restaurants/${restaurantId}/api-key`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        navigator.clipboard.writeText(data.api_key);
        toast.success('API Key generada y copiada');
      }
    } catch (error) {
      toast.error('Error al generar API Key');
    }
  };

  const resetPin = async (restaurantId) => {
    if (!confirm('¿Resetear PIN del restaurante a 9999?')) return;
    
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_URL}/api/admin/restaurants/${restaurantId}/reset-pin`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        toast.success('PIN reseteado a 9999');
        fetchRestaurants();
      }
    } catch (error) {
      toast.error('Error al resetear PIN');
    }
  };

  // Helper: extract a human-readable error from any FastAPI response
  const extractError = (data) => {
    if (!data) return 'Error desconocido';
    if (typeof data.detail === 'string') return data.detail;
    // FastAPI 422 returns detail as an array of validation errors
    if (Array.isArray(data.detail)) {
      return data.detail.map(e => `${e.loc?.slice(-1)[0] || 'campo'}: ${e.msg}`).join(' · ');
    }
    if (data.message) return data.message;
    return JSON.stringify(data);
  };

  const handleCreateRestaurant = async () => {
    if (!newRestaurant.name?.trim()) { toast.error('El nombre del restaurante es obligatorio'); return; }
    if (!newRestaurant.owner_name?.trim()) { toast.error('El nombre del propietario es obligatorio'); return; }
    if (!newRestaurant.owner_email?.trim()) { toast.error('El email del propietario es obligatorio'); return; }
    
    setCreating(true);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_URL}/api/admin/restaurants`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          name: newRestaurant.name.trim(),
          owner_name: newRestaurant.owner_name.trim(),
          owner_email: newRestaurant.owner_email.trim().toLowerCase(),
          timezone: newRestaurant.timezone || 'America/Mexico_City',
          currency: newRestaurant.currency || 'MXN'
        })
      });
      
      let data;
      try { data = await response.json(); } catch { data = {}; }

      if (response.ok) {
        const tempPwd = data.temp_password;
        toast.success(
          tempPwd
            ? `✅ Restaurante creado. Contraseña temporal del dueño: ${tempPwd}`
            : `Restaurante "${newRestaurant.name}" creado exitosamente`,
          { duration: tempPwd ? 10000 : 4000 }
        );
        setShowCreateDialog(false);
        setNewRestaurant({ name: '', owner_name: '', owner_email: '', timezone: 'America/Mexico_City', currency: 'MXN' });
        fetchRestaurants();
      } else {
        toast.error(extractError(data));
      }
    } catch (error) {
      console.error('Create restaurant error:', error);
      toast.error('No se pudo conectar al servidor. Verifica que el backend esté activo.');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="min-h-screen" data-testid="admin-restaurants-page">
      <header className="sticky top-0 z-30 h-14 glass flex items-center justify-between px-6">
        <h1 className="text-lg font-semibold">Restaurantes</h1>
        <div className="flex items-center gap-3">
          <Badge variant="outline">{restaurants.length} total</Badge>
          <Button onClick={() => setShowCreateDialog(true)} data-testid="create-restaurant-btn">
            <Plus className="h-4 w-4 mr-2" />
            Nuevo Restaurante
          </Button>
        </div>
      </header>

      <div className="p-6">
        {/* Search */}
        <div className="mb-6">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar restaurantes..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
              data-testid="search-restaurants-input"
            />
          </div>
        </div>

        {/* Table */}
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Restaurante</TableHead>
                <TableHead className="text-center">Mesas</TableHead>
                <TableHead className="text-center">Reservaciones</TableHead>
                <TableHead className="text-center">Stripe</TableHead>
                <TableHead className="text-center">Estado</TableHead>
                <TableHead className="w-40">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {restaurants.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                    No hay restaurantes
                  </TableCell>
                </TableRow>
              ) : (
                restaurants.map((restaurant) => (
                  <TableRow key={restaurant.id} data-testid={`restaurant-row-${restaurant.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
                          <Building2 className="h-4 w-4 text-primary" />
                        </div>
                        <div>
                          <p className="font-medium">{restaurant.name}</p>
                          <p className="text-xs text-muted-foreground">{restaurant.id.slice(0, 8)}...</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-center">{restaurant.total_tables || 0}</TableCell>
                    <TableCell className="text-center">{restaurant.total_reservations || 0}</TableCell>
                    <TableCell className="text-center">
                      {restaurant.stripe_connected ? (
                        <Badge 
                          className="bg-green-500/10 text-green-600 cursor-pointer" 
                          onClick={() => openStripeDashboard(restaurant.id)}
                          title="Abrir Dashboard de Stripe"
                        >
                          Conectado
                        </Badge>
                      ) : restaurant.stripe_account_id ? (
                        <Badge 
                          variant="secondary" 
                          className="cursor-pointer"
                          onClick={() => checkStripeStatus(restaurant.id)}
                          title="Verificar estado"
                        >
                          Pendiente
                        </Badge>
                      ) : (
                        <Badge variant="outline">No</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => toggleActive(restaurant.id, restaurant.is_active)}
                      >
                        {restaurant.is_active ? (
                          <ToggleRight className="h-5 w-5 text-green-500" />
                        ) : (
                          <ToggleLeft className="h-5 w-5 text-muted-foreground" />
                        )}
                      </Button>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => viewDetails(restaurant.id)}
                          title="Ver detalles"
                          data-testid={`view-restaurant-${restaurant.id}`}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => restaurant.stripe_connected ? openStripeDashboard(restaurant.id) : connectStripe(restaurant.id)}
                          title={restaurant.stripe_connected ? "Ver Dashboard Stripe" : "Conectar Stripe"}
                        >
                          <CreditCard className={`h-4 w-4 ${restaurant.stripe_connected ? 'text-green-500' : ''}`} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => generateApiKey(restaurant.id)}
                          title="Generar API Key"
                        >
                          <Key className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => resetPin(restaurant.id)}
                          title="Resetear PIN"
                        >
                          <RefreshCw className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Card>
      </div>

      {/* Create Restaurant Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-md" data-testid="create-restaurant-dialog">
          <DialogHeader>
            <DialogTitle>Crear Nuevo Restaurante</DialogTitle>
            <DialogDescription>
              El propietario recibirá las credenciales por email y establecerá su PIN en el primer login.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="restaurant-name">Nombre del Restaurante *</Label>
              <Input
                id="restaurant-name"
                placeholder="Mi Restaurante"
                value={newRestaurant.name}
                onChange={(e) => setNewRestaurant({ ...newRestaurant, name: e.target.value })}
                data-testid="new-restaurant-name"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="owner-name">Nombre del Propietario *</Label>
              <Input
                id="owner-name"
                placeholder="Juan Pérez"
                value={newRestaurant.owner_name}
                onChange={(e) => setNewRestaurant({ ...newRestaurant, owner_name: e.target.value })}
                data-testid="new-restaurant-owner-name"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="owner-email">Email del Propietario *</Label>
              <Input
                id="owner-email"
                type="email"
                placeholder="correo@restaurante.com"
                value={newRestaurant.owner_email}
                onChange={(e) => setNewRestaurant({ ...newRestaurant, owner_email: e.target.value })}
                data-testid="new-restaurant-owner-email"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Zona Horaria</Label>
                <Select
                  value={newRestaurant.timezone}
                  onValueChange={(value) => setNewRestaurant({ ...newRestaurant, timezone: value })}
                >
                  <SelectTrigger data-testid="new-restaurant-timezone">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="America/Mexico_City">Ciudad de México</SelectItem>
                    <SelectItem value="America/Monterrey">Monterrey</SelectItem>
                    <SelectItem value="America/Cancun">Cancún</SelectItem>
                    <SelectItem value="America/Tijuana">Tijuana</SelectItem>
                    <SelectItem value="America/Bogota">Bogotá</SelectItem>
                    <SelectItem value="America/Lima">Lima</SelectItem>
                    <SelectItem value="America/Santiago">Santiago</SelectItem>
                    <SelectItem value="Europe/Madrid">Madrid</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Moneda</Label>
                <Select
                  value={newRestaurant.currency}
                  onValueChange={(value) => setNewRestaurant({ ...newRestaurant, currency: value })}
                >
                  <SelectTrigger data-testid="new-restaurant-currency">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="MXN">MXN (Peso Mexicano)</SelectItem>
                    <SelectItem value="USD">USD (Dólar)</SelectItem>
                    <SelectItem value="EUR">EUR (Euro)</SelectItem>
                    <SelectItem value="COP">COP (Peso Colombiano)</SelectItem>
                    <SelectItem value="PEN">PEN (Sol Peruano)</SelectItem>
                    <SelectItem value="CLP">CLP (Peso Chileno)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleCreateRestaurant} 
              disabled={creating}
              data-testid="submit-create-restaurant"
            >
              {creating && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Crear Restaurante
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Details Dialog */}
      <Dialog open={showDetailsDialog} onOpenChange={setShowDetailsDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="restaurant-details-dialog">
          <DialogHeader>
            <DialogTitle>Detalles del Restaurante</DialogTitle>
          </DialogHeader>

          {restaurantDetails && (
            <div className="space-y-6">
              {/* Restaurant Info */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Nombre</p>
                  <p className="font-medium text-lg">{restaurantDetails.restaurant.name}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">ID</p>
                  <code className="text-xs bg-muted p-1 rounded block mt-1">{restaurantDetails.restaurant.id}</code>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Estado</p>
                  <Badge className={restaurantDetails.restaurant.is_active ? 'bg-green-500/10 text-green-600' : 'bg-red-500/10 text-red-600'}>
                    {restaurantDetails.restaurant.is_active ? 'Activo' : 'Inactivo'}
                  </Badge>
                </div>
              </div>

              {/* Contact & Location */}
              <div className="grid grid-cols-3 gap-4 p-4 bg-muted/50 rounded-lg">
                <div>
                  <p className="text-sm text-muted-foreground">Email</p>
                  <p className="font-medium">{restaurantDetails.restaurant.email || 'No especificado'}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Teléfono</p>
                  <p className="font-medium">{restaurantDetails.restaurant.phone || 'No especificado'}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Dirección</p>
                  <p className="font-medium">{restaurantDetails.restaurant.address || 'No especificada'}</p>
                </div>
              </div>

              {/* Settings */}
              <div className="grid grid-cols-4 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Moneda</p>
                  <p className="font-medium">{restaurantDetails.restaurant.currency}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Zona Horaria</p>
                  <p className="font-medium">{restaurantDetails.restaurant.timezone}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">PIN Actual</p>
                  <p className="font-medium font-mono">{restaurantDetails.restaurant.pin || '****'}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">PIN Configurado</p>
                  <Badge variant={restaurantDetails.restaurant.pin_set ? 'default' : 'secondary'}>
                    {restaurantDetails.restaurant.pin_set ? 'Sí' : 'No'}
                  </Badge>
                </div>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-4 gap-4">
                <Card>
                  <CardContent className="p-3 text-center">
                    <p className="text-2xl font-bold">{restaurantDetails.tables_count}</p>
                    <p className="text-xs text-muted-foreground">Mesas</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-3 text-center">
                    <p className="text-2xl font-bold">{restaurantDetails.stats.total_reservations}</p>
                    <p className="text-xs text-muted-foreground">Reservaciones</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-3 text-center">
                    <p className="text-2xl font-bold">${restaurantDetails.stats.total_revenue.toLocaleString()}</p>
                    <p className="text-xs text-muted-foreground">Revenue</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-3 text-center">
                    <p className="text-2xl font-bold">{restaurantDetails.stats.total_events || 0}</p>
                    <p className="text-xs text-muted-foreground">Eventos</p>
                  </CardContent>
                </Card>
              </div>

              {/* Stripe Status */}
              <div className="p-4 bg-muted/50 rounded-lg">
                <p className="text-sm font-medium mb-2">Stripe Connect</p>
                <div className="flex items-center gap-4">
                  <Badge className={restaurantDetails.restaurant.stripe_connected ? 'bg-green-500/10 text-green-600' : 'bg-yellow-500/10 text-yellow-600'}>
                    {restaurantDetails.restaurant.stripe_connected ? 'Conectado' : 'No conectado'}
                  </Badge>
                  {restaurantDetails.restaurant.stripe_account_id && (
                    <code className="text-xs bg-muted p-1 rounded">{restaurantDetails.restaurant.stripe_account_id}</code>
                  )}
                </div>
              </div>

              {/* Users */}
              <div>
                <p className="text-sm font-medium mb-2">Usuarios ({restaurantDetails.users.length})</p>
                <div className="space-y-1">
                  {restaurantDetails.users.map((user) => (
                    <div key={user.id} className="flex items-center justify-between p-2 rounded bg-muted text-sm">
                      <div>
                        <span className="font-medium">{user.name}</span>
                        <span className="text-muted-foreground ml-2">({user.email})</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline">{user.role}</Badge>
                        {user.pin_set ? (
                          <Badge className="bg-green-500/10 text-green-600">PIN activo</Badge>
                        ) : (
                          <Badge variant="secondary">Sin PIN</Badge>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Deposit Settings */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-muted/50 rounded-lg">
                  <p className="text-sm font-medium mb-2">Configuración de Depósitos</p>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div className="p-2 rounded bg-background">
                      <span className="text-muted-foreground">Habilitado:</span>{' '}
                      {restaurantDetails.restaurant.deposit_enabled ? 'Sí' : 'No'}
                    </div>
                    <div className="p-2 rounded bg-background">
                      <span className="text-muted-foreground">Porcentaje:</span>{' '}
                      {restaurantDetails.restaurant.deposit_percentage}%
                    </div>
                  </div>
                </div>
                <div className="p-4 bg-muted/50 rounded-lg">
                  <p className="text-sm font-medium mb-2">Fechas</p>
                  <div className="grid grid-cols-1 gap-2 text-sm">
                    <div className="p-2 rounded bg-background">
                      <span className="text-muted-foreground">Creado:</span>{' '}
                      {new Date(restaurantDetails.restaurant.created_at).toLocaleDateString()}
                    </div>
                    {restaurantDetails.restaurant.updated_at && (
                      <div className="p-2 rounded bg-background">
                        <span className="text-muted-foreground">Actualizado:</span>{' '}
                        {new Date(restaurantDetails.restaurant.updated_at).toLocaleDateString()}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          <DialogFooter className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={() => {
                if (restaurantDetails) resetPin(restaurantDetails.restaurant.id);
              }}
            >
              <RefreshCw className="h-4 w-4 mr-2" /> Resetear PIN a 9999
            </Button>
            <Button variant="outline" onClick={() => setShowDetailsDialog(false)}>
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
